import React, { useEffect, useMemo, useState } from 'react'
import { Alert, notification, Modal } from 'antd'
import { RouteComponentProps } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { ApplicationState } from 'src/store'
import { ButtonTitled, PageHeader } from 'src/components/Ant'
import {
  getClosings,
  setClosingStatus
} from 'src/core/store/ducks/closings/actions'
import { getFullRequest } from 'src/core/store/ducks/requests/actions'
import { IconEditRequestTitle } from 'src/components/atoms/edit-request-title'
import { RequestClosingStatus, RequestUserData } from 'src/types'
import SegmentControl, { Segments } from 'src/components/SegmentControl'
import useReasonPolicyRule from 'src/core/hooks/use-reason-policy-rule'
import * as CSS from './styles'
import { shouldShowRDVButton } from '../utils'
import ModalAction, { ModalActionType } from '../../components/modal-action'
import RDVButton from '../../components/RDVButton'
import Receipts from '../../components/Receipts'
import RequestSummary from '../../components/RequestSummary'

interface Params {
  request_id?: string
}

type SegmentKeys = 'receipts' | 'summary'

type SummarySegments = Segments<SegmentKeys>

function ClosingsSummary({
  history,
  match: {
    params: { request_id }
  }
}: RouteComponentProps<Params>) {
  const {
    closings,
    me: {
      user: { auditor, role_group, tenant_id, user_level_type }
    },
    requests
  } = useSelector((state: ApplicationState) => ({
    closings: state.closings,
    me: state.me,
    requests: state.requests
  }))

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [action, setAction] = useState<ModalActionType | null>(null)
  const [current, setCurrent] = useState<SegmentKeys>('summary')
  const [changingStatus, setChangingStatus] = useState<boolean>(false)

  const policyRule = useReasonPolicyRule(
    requests.fullRequest?.request_user_data[0].reason_id
  )

  const isSuperAdmin = ['SUPER_ADMIN'].includes(user_level_type)

  const enableCancelAction = ['CLOSING_PENDING', 'CLOSING_REPROVED'].includes(
    requests.fullRequest?.status
  )
  const enableApprovalAction = ['CLOSING_PENDING', 'UNDERWAY'].includes(
    requests.fullRequest?.status
  )
  const enableReproveAction = requests.fullRequest?.status === 'CLOSING_PENDING'

  const segments = useMemo<SummarySegments>(
    () => ({
      summary: {
        disabled: false,
        title: 'web.summary.title',
        renderContent: () => (
          <RequestSummary
            enableBudget={policyRule?.enable_budget}
            enableSharingEdit={
              isSuperAdmin ? isSuperAdmin : enableApprovalAction
            }
            originRequest="closings"
            request={requests.fullRequest}
          />
        )
      },
      receipts: {
        disabled: false,
        title: 'web.receipts.title',
        renderContent: () => (
          <Receipts closings request={requests.fullRequest} />
        )
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requests.fullRequest]
  )

  useEffect(() => {
    dispatch(getClosings({ page: 1, request_ids: [request_id] }))
    dispatch(getFullRequest(request_id, 'closings'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request_id])

  useEffect(() => {
    if (closings.setStatusLoading !== changingStatus) {
      setChangingStatus(closings.setStatusLoading)

      if (!closings.setStatusLoading) {
        if (closings.setStatusError) {
          notification.error({
            duration: 5,
            message: closings.setStatusError,
            placement: 'bottomRight'
          })
        } else {
          notification.success({
            duration: 7,
            message: closings.setStatusSuccess,
            placement: 'bottomRight'
          })
          history.push('/closings')
        }

        Modal.destroyAll()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closings.setStatusLoading])

  function toggleAction(action: 'approve' | 'cancel' | 'disapprove' | null) {
    if (!action) {
      setAction(null)
      return
    }

    let actionTitle = ''
    switch (action) {
      case 'approve':
        actionTitle = t('web.closings.modal-accept')
        break
      case 'disapprove':
        actionTitle = t('web.closings.modal-decline')
        break
      case 'cancel':
        actionTitle = t('web.closings.modal-cancel')
    }

    setAction({
      action,
      title: actionTitle
    })
  }

  function userReduce(acc: RequestUserData[], cur: RequestUserData) {
    const canApprove =
      (cur.status === 'CLOSING_PENDING' && cur.closing) ||
      (cur.status === 'CLOSING_REPROVED' && cur.allowed)

    if (!canApprove) {
      return acc
    }

    const user_data = cur.request_closings.filter(
      closing => closing.status === 'PENDING'
    )
    return [...acc, { ...cur, request_closings: user_data }]
  }

  function handleSubmit(justification: string, users: RequestUserData[]) {
    let status: RequestClosingStatus
    switch (action.action) {
      case 'approve':
        status = 'APPROVED'
        break
      case 'cancel':
        status = 'CANCELED'
        break
      case 'disapprove':
        status = 'REPROVED'
        break
    }

    if (!status) {
      return
    }

    dispatch(
      setClosingStatus({
        justification,
        request_closing_ids: users.reduce(
          (acc, { request_closings }) => [
            ...acc,
            ...request_closings
              .filter(
                ({ situation, status: request_closing_status }) =>
                  situation === 'ACTIVE' && request_closing_status === 'PENDING'
              )
              .map(({ request_closing_id }) => request_closing_id)
          ],
          []
        ),
        request_id,
        status
      })
    )
  }

  if (requests.fullRequestLoading || closings.loading) {
    return <CSS.Spin />
  }

  const hasError = requests.fullRequestError || closings.error
  if (hasError) {
    return <Alert message={hasError} showIcon type="error" />
  }

  if (!requests.fullRequest) {
    const message = t('common.response.unexpected-error')
    return <Alert message={message} showIcon type="error" />
  }

  const canAuditorEdit =
    auditor && [334081949181, 597990758089].includes(+tenant_id)
  return (
    <>
      <PageHeader
        extra={[
          enableCancelAction && (
            <ButtonTitled
              data-test-id="summary-closing-cancel"
              icon="delete"
              key="btnCancel"
              onClick={() => toggleAction('cancel')}
              size="default"
              tooltip="web.closings.cancel"
              type="default"
            />
          ),
          enableReproveAction && (
            <ButtonTitled
              data-test-id="summary-closing-disapprove"
              icon="close"
              key="btnDisapprove"
              onClick={() => toggleAction('disapprove')}
              size="default"
              tooltip="web.closings.decline"
              type="danger"
            />
          ),
          enableApprovalAction && (
            <ButtonTitled
              data-test-id="summary-closing-submit"
              icon="check"
              key="btnApprove"
              onClick={() => toggleAction('approve')}
              size="default"
              tooltip="web.closings.accept"
            />
          ),
          shouldShowRDVButton({
            request: requests.fullRequest,
            roleGroup: role_group
          }) && <RDVButton key="btnRDV" request={requests.fullRequest} />
        ]}
        onBack={() => history.goBack()}
        subTitle={
          <>
            {`#${requests.fullRequest.request_number} - ${requests.fullRequest.title} `}
            {canAuditorEdit && (
              <IconEditRequestTitle
                requestId={requests.fullRequest.request_id}
                organizationId={requests.fullRequest.organization_id}
                update={() => dispatch(getFullRequest(request_id, 'closings'))}
                title={requests.fullRequest.title}
              />
            )}
          </>
        }
        title="web.closings.title"
      />
      <CSS.ContainerSegmentControl>
        <SegmentControl
          defaultValue="summary"
          onChange={({ target: { value } }) => {
            setCurrent(value)
          }}
          segments={segments}
          value={current}
        />
      </CSS.ContainerSegmentControl>
      {segments[current].renderContent()}
      <ModalAction
        handleSubmit={handleSubmit}
        isLoading={
          closings.loading ||
          closings.setStatusLoading ||
          requests.fullRequestLoading
        }
        modalAction={action}
        request_user_data={requests.fullRequest?.request_user_data}
        toggleAction={toggleAction}
        userReduce={userReduce}
      />
    </>
  )
}

export default ClosingsSummary
